import { IBannerModel } from '@shared/models/alerts/IBannerModel';
import { useEffect, useState } from 'react';
import { createContainer } from 'unstated-next';
import { subscribe } from '../helpers/EventCallback';

function useAlertsContainer() {
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [bannerMap, setBannerMap] = useState(new Map<string, IBannerModel>());
    const bannersArray = Array.from(bannerMap.values());

    const showError = (error: string, timeout: number = 5000) => {
        setError(error);
        setTimeout(() => {
            setError(null);
        }, timeout);
    };
    const showSuccess = (success: string, timeout: number = 5000) => {
        setSuccess(success);
        setTimeout(() => {
            setSuccess(null);
        }, timeout);
    };

    const showWarning = (warning: string, timeout: number = 5000) => {
        setWarning(warning);
        setTimeout(() => {
            setWarning(null);
        }, timeout);
    };

    const addBanner = (newBanner: IBannerModel, duration?: number) => {
        setBannerMap((prevBannerMap) => {
            const updatedMap = new Map(prevBannerMap);

            // Clear the existing timeout if the banner already exists
            if (updatedMap.has(newBanner.id)) {
                const existingBanner = updatedMap.get(newBanner.id);
                if (existingBanner && existingBanner.timeout) {
                    clearTimeout(existingBanner.timeout);
                }
            }

            let timeout: NodeJS.Timeout | undefined;

            // Set a new timeout to remove the banner if duration is provided
            if (duration !== undefined) {
                timeout = setTimeout(() => {
                    setBannerMap((currentMap) => {
                        const newMap = new Map(currentMap);
                        newMap.delete(newBanner.id);
                        return newMap;
                    });
                }, duration);
            }

            // Add the banner with the timeout if it exists
            updatedMap.set(newBanner.id, { ...newBanner, timeout });

            return updatedMap;
        });
    };

    const removeBanner = (id: string) => {
        setBannerMap((prevBannerMap) => {
            const updatedMap = new Map(prevBannerMap);
            const banner = updatedMap.get(id);
            if (banner && banner.timeout) {
                clearTimeout(banner.timeout);
            }
            updatedMap.delete(id);

            return updatedMap;
        });
    };

    useEffect(() => {
        const unsubscribeSuccess = subscribe('alertSuccess', (alert) => {
            showSuccess(alert.message);
        });
        const unsubscribeError = subscribe('alertError', (alert) => {
            showError(alert.message);
        });
        const unsubscribeWarning = subscribe('alertWarning', (alert) => {
            showWarning(alert.message);
        });

        return () => {
            unsubscribeSuccess();
            unsubscribeError();
            unsubscribeWarning();
        };
    }, []);

    return {
        showSuccess,
        showError,
        showWarning,
        bannersArray,
        addBanner,
        removeBanner,
        success,
        error,
        warning,
    };
}

const AlertsContainer = createContainer(useAlertsContainer);
export default AlertsContainer;
